import { useState } from 'react';
import { useEffect } from 'react';
import jsPDF from 'jspdf';
import {
  requestConsolidatedReport,
  checkConsolidatedReportStatus,
  extractErrorMessages,
} from '../../api/brandingApi';
import type {
  ConsolidatedReportCreateResponse,
  ConsolidatedReportStatusResponse,
  BrandingAnalyzeResponse,
  CheckResult,
} from '../../api/brandingApi';
import ScanResultSummary from './ScanResultSummary';
import CheckCategoryAccordion from './CheckCategoryAccordion';
import PublicCtaBox from './PublicCtaBox';

// ── helpers ───────────────────────────────────────────────────────────────────

const SCAN_LABELS: Record<string, string> = {
  SITE:        'Website Health Check',
  PAGE:        'Page SEO Analyzer',
  ROBOTS:      'robots.txt Validator',
  SITEMAP:     'Sitemap Validator',
  GSC_OWNER:   'GSC Verification Signals',
  SCHEMA:      'Schema / JSON-LD Detector',
  AI_CRAWLING: 'AI Search Readiness',
};

const SCAN_ORDER = ['SITE', 'PAGE', 'ROBOTS', 'SITEMAP', 'GSC_OWNER', 'SCHEMA', 'AI_CRAWLING'];
const MAX_ALLOWED_TRIES = 4;
const ASNERIS_LOGO_PATH = '/asneris.png';
const SEVERITY_WEIGHT: Record<string, number> = { HIGH: 4, MEDIUM: 3, LOW: 2, INFO: 1 };
const CONSOLIDATED_LEARN_MORE_HREF = '/seo-concepts/consolidated-report/';

function isValidUrl(v: string) {
  return /^https?:\/\/[^\s/$.?#].[^\s]*$/.test(v.trim());
}

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

function formatDateTime(iso: string) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function scoreColor(score: number) {
  if (score >= 75) return '#15803d';
  if (score >= 55) return '#d97706';
  return '#dc2626';
}

function scoreLabel(score: number) {
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 55) return 'Needs work';
  return 'Poor';
}

function formatCheckName(name: string) {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function orderedScanEntries(scanResults?: Record<string, BrandingAnalyzeResponse>) {
  const scans = scanResults ?? {};
  return [
    ...SCAN_ORDER.filter((scanType) => scans[scanType]).map((scanType) => [scanType, scans[scanType]] as const),
    ...Object.entries(scans).filter(([scanType]) => !SCAN_ORDER.includes(scanType)),
  ];
}

function estimatedPotentialScore(scan: BrandingAnalyzeResponse) {
  const checks = scan.checks ?? [];
  if (!checks.length) return scan.overallScore;
  const recoverableAverage = checks.reduce((sum, check) => {
    if (check.status === 'PASS') return sum;
    return sum + Math.max(0, 100 - (check.score ?? 0));
  }, 0) / checks.length;
  return Math.min(100, Math.round(scan.overallScore + recoverableAverage));
}

function buildScanSummary(scan: BrandingAnalyzeResponse) {
  const checks = scan.checks ?? [];
  const topIssues = checks
    .filter((check) => check.status === 'FAIL' || check.status === 'WARNING')
    .sort((a, b) => {
      const severityDifference = (SEVERITY_WEIGHT[b.severity] ?? 0) - (SEVERITY_WEIGHT[a.severity] ?? 0);
      if (severityDifference !== 0) return severityDifference;
      return (a.score ?? 0) - (b.score ?? 0);
    });

  return {
    potentialScore: estimatedPotentialScore(scan),
    passed: checks.filter((check) => check.status === 'PASS').length,
    warnings: checks.filter((check) => check.status === 'WARNING').length,
    failed: checks.filter((check) => check.status === 'FAIL').length,
    topIssues,
  };
}

function buildConsolidatedSummary(scanResults?: Record<string, BrandingAnalyzeResponse>) {
  const entries = orderedScanEntries(scanResults);
  const scans = entries.map(([, scan]) => scan);
  const allChecks = entries.flatMap(([scanType, scan]) =>
    (scan.checks ?? []).map((check) => ({ scanType, scanLabel: SCAN_LABELS[scanType] ?? scanType, check })),
  );
  const topIssues = allChecks
    .filter(({ check }) => check.status === 'FAIL' || check.status === 'WARNING')
    .sort((a, b) => {
      const severityDifference =
        (SEVERITY_WEIGHT[b.check.severity] ?? 0) - (SEVERITY_WEIGHT[a.check.severity] ?? 0);
      if (severityDifference !== 0) return severityDifference;
      return (a.check.score ?? 0) - (b.check.score ?? 0);
    });
  const averageScore = scans.length
    ? Math.round(scans.reduce((sum, scan) => sum + scan.overallScore, 0) / scans.length)
    : 0;
  const potentialScore = scans.length
    ? Math.round(scans.reduce((sum, scan) => sum + estimatedPotentialScore(scan), 0) / scans.length)
    : averageScore;

  return {
    entries,
    averageScore,
    potentialScore,
    passed: allChecks.filter(({ check }) => check.status === 'PASS').length,
    warnings: allChecks.filter(({ check }) => check.status === 'WARNING').length,
    failed: allChecks.filter(({ check }) => check.status === 'FAIL').length,
    topIssues,
  };
}

async function loadImageDataUrl(src: string): Promise<string | null> {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function writeWrappedText(pdf: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number) {
  const lines = pdf.splitTextToSize(text, maxWidth) as string[];
  pdf.text(lines, x, y);
  return y + lines.length * lineHeight;
}

function ensurePdfSpace(pdf: jsPDF, y: number, neededHeight: number) {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (y + neededHeight <= pageHeight - 26) return y;
  pdf.addPage();
  return 32;
}

function checkStatusLabel(status: string) {
  if (status === 'PASS') return 'Pass';
  if (status === 'WARNING') return 'Warning';
  if (status === 'FAIL') return 'Issue';
  return status || 'Info';
}

function drawPdfTableHeader(pdf: jsPDF, margin: number, y: number, contentWidth: number) {
  const columns = [
    { label: 'Check', x: margin + 3 },
    { label: 'Status', x: margin + 34 },
    { label: 'Severity', x: margin + 53 },
    { label: 'Score', x: margin + 75 },
    { label: 'Detail', x: margin + 93 },
    { label: 'Recommendation', x: margin + 136 },
  ];

  pdf.setFillColor(248, 251, 254);
  pdf.setDrawColor(218, 228, 240);
  pdf.rect(margin, y, contentWidth, 8, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(6.8);
  pdf.setTextColor(82, 102, 132);
  columns.forEach((column) => pdf.text(column.label.toUpperCase(), column.x, y + 5.2));
  return y + 8;
}

function drawPdfDetailTableRow(
  pdf: jsPDF,
  check: CheckResult,
  margin: number,
  y: number,
  contentWidth: number,
) {
  const recommendation =
    (check.status === 'FAIL' || check.status === 'WARNING') && check.recommendation
      ? check.recommendation
      : '';
  const checkLines = (pdf.splitTextToSize(formatCheckName(check.checkName), 27) as string[]).slice(0, 3);
  const detailLines = pdf.splitTextToSize(check.message ?? '', 39) as string[];
  const recommendationLines = pdf.splitTextToSize(recommendation, contentWidth - 141) as string[];
  const rowHeight = Math.max(13, checkLines.length * 4 + 6, detailLines.length * 4 + 6, recommendationLines.length * 4 + 6);

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(228, 237, 246);
  pdf.rect(margin, y, contentWidth, rowHeight, 'FD');
  pdf.setDrawColor(238, 244, 250);
  [margin + 32, margin + 51, margin + 73, margin + 91, margin + 134].forEach((x) => {
    pdf.line(x, y, x, y + rowHeight);
  });

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.4);
  pdf.setTextColor(15, 54, 99);
  pdf.text(checkLines, margin + 3, y + 5);

  pdf.setFontSize(6.8);
  pdf.setTextColor(scoreColor(check.score ?? 0));
  pdf.text(checkStatusLabel(check.status), margin + 34, y + 5);
  pdf.text(check.severity, margin + 53, y + 5);
  pdf.text(`${check.score ?? 0}/100`, margin + 89, y + 5, { align: 'right' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.2);
  pdf.setTextColor(47, 65, 92);
  pdf.text(detailLines, margin + 93, y + 5);
  pdf.setTextColor(check.status === 'FAIL' ? 153 : check.status === 'WARNING' ? 146 : 82, check.status === 'FAIL' ? 27 : check.status === 'WARNING' ? 64 : 102, check.status === 'FAIL' ? 27 : check.status === 'WARNING' ? 14 : 132);
  pdf.text(recommendationLines, margin + 136, y + 5);

  return y + rowHeight;
}

function drawPdfPassedSummary(
  pdf: jsPDF,
  checks: CheckResult[],
  margin: number,
  y: number,
  contentWidth: number,
) {
  if (!checks.length) return y;
  const passedNames = checks.map((check) => formatCheckName(check.checkName)).join(', ');
  const lines = pdf.splitTextToSize(`Passed checks (${checks.length}): ${passedNames}`, contentWidth - 10) as string[];
  const height = Math.max(10, lines.length * 4 + 6);
  y = ensurePdfSpace(pdf, y, height + 3);
  pdf.setFillColor(240, 253, 244);
  pdf.setDrawColor(187, 247, 208);
  pdf.roundedRect(margin, y, contentWidth, height, 2, 2, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(21, 128, 61);
  pdf.text(lines, margin + 5, y + 6);
  return y + height + 4;
}

function drawConsolidatedActionPlan(
  pdf: jsPDF,
  issues: Array<{ scanLabel: string; check: CheckResult }>,
  margin: number,
  y: number,
  contentWidth: number,
) {
  y = ensurePdfSpace(pdf, y, 22);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Recommended Action Plan', margin, y);
  y += 8;

  if (!issues.length) {
    pdf.setFillColor(240, 253, 244);
    pdf.setDrawColor(187, 247, 208);
    pdf.roundedRect(margin, y, contentWidth, 14, 2, 2, 'FD');
    pdf.setFontSize(8);
    pdf.setTextColor(22, 101, 52);
    pdf.text('No priority fixes are required across the completed diagnostics.', margin + 5, y + 8);
    return y + 20;
  }

  issues.slice(0, 5).forEach(({ scanLabel, check }, index) => {
    const recommendation = check.recommendation || check.message || 'Review this finding and apply the recommended fix.';
    const lines = pdf.splitTextToSize(recommendation, contentWidth - 58) as string[];
    const rowHeight = Math.max(18, lines.length * 4 + 10);
    y = ensurePdfSpace(pdf, y, rowHeight + 4);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(margin, y, contentWidth, rowHeight, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(scoreColor(check.score ?? 0));
    pdf.text(`Priority ${index + 1}`, margin + 5, y + 7);
    pdf.setTextColor(15, 54, 99);
    pdf.text(`${scanLabel}: ${formatCheckName(check.checkName)}`, margin + 27, y + 7);
    pdf.setTextColor(82, 102, 132);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Impact: ${check.severity} | Effort: Review`, margin + 5, y + 14);
    pdf.text(lines, margin + 57, y + 14);
    y += rowHeight + 4;
  });

  return y + 4;
}

function drawConsolidatedPdfBranding(
  pdf: jsPDF,
  pageNumber: number,
  totalPages: number,
  title: string,
  logoDataUrl: string | null,
) {
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const generatedAt = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, 0, pageWidth, 24, 'F');
  if (logoDataUrl) {
    pdf.addImage(logoDataUrl, 'PNG', 10, 6, 16, 12);
  } else {
    pdf.setFillColor(15, 54, 99);
    pdf.circle(14, 12, 5, 'F');
    pdf.setTextColor(255, 255, 255);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('A', 14, 14.5, { align: 'center' });
  }
  pdf.setTextColor(15, 54, 99);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.text('Asneris', 30, 10.5);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text(title, 30, 16);
  pdf.text(`Generated ${generatedAt}`, pageWidth - 10, 16, { align: 'right' });
  pdf.setDrawColor(218, 228, 240);
  pdf.line(10, 23, pageWidth - 10, 23);

  pdf.setFillColor(255, 255, 255);
  pdf.rect(0, pageHeight - 20, pageWidth, 20, 'F');
  pdf.setDrawColor(218, 228, 240);
  pdf.line(10, pageHeight - 19, pageWidth - 10, pageHeight - 19);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Disclaimer: This report is informational only and does not guarantee search rankings, AI visibility, revenue, or platform outcomes.', 10, pageHeight - 12);
  pdf.setFontSize(8);
  pdf.text('Asneris - Accurate. Actionable. Free.', 10, pageHeight - 6);
  pdf.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 10, pageHeight - 6, { align: 'right' });
}

function generateConsolidatedPdf(
  result: ConsolidatedReportStatusResponse,
  email: string,
  logoDataUrl: string | null,
) {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const summary = buildConsolidatedSummary(result.scanResults);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const margin = 14;
  const contentWidth = pageWidth - margin * 2;
  const title = 'Full SEO Diagnostic Report';
  let y = 34;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(15, 54, 99);
  pdf.text(title, margin, y);
  y += 8;

  pdf.setFillColor(248, 251, 254);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(margin, y, contentWidth, 32, 3, 3, 'FD');
  pdf.setFontSize(11);
  pdf.text('Report prepared for', margin + 6, y + 8);
  pdf.setFontSize(13);
  pdf.text(result.businessName || result.name || 'Website owner', margin + 6, y + 16);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(82, 102, 132);
  pdf.text(`Reference: ${result.referenceNumber}   |   Email: ${email}`, margin + 6, y + 24);
  y += 42;

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(margin, y, contentWidth, 28, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(scoreColor(summary.averageScore));
  pdf.text(`${summary.averageScore}/100`, margin + 6, y + 11);
  pdf.setFontSize(10);
  pdf.setTextColor(15, 54, 99);
  pdf.text(`${summary.entries.length} reports included`, margin + 56, y + 11);
  pdf.text(`${summary.passed} passed`, margin + 104, y + 11);
  pdf.text(`${summary.warnings} warnings`, margin + 140, y + 11);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(82, 102, 132);
  pdf.text(`${summary.failed} issues found across all diagnostics`, margin + 56, y + 20);
  y += 40;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Consolidated Executive Summary', margin, y);
  y += 8;

  const cardGap = 6;
  const cardWidth = (contentWidth - cardGap) / 2;
  const topRows = summary.topIssues.slice(0, 5).map((issue) => ({
    issue,
    messageLines: (pdf.splitTextToSize(issue.check.message ?? '', cardWidth - 16) as string[]).slice(0, 2),
  }));
  const topHeight = topRows.length ? topRows.reduce((sum, row) => sum + 7 + row.messageLines.length * 4 + 2, 0) : 9;
  const execHeight = Math.max(72, 24 + topHeight);
  const leftX = margin;
  const rightX = margin + cardWidth + cardGap;
  const cardY = y;

  pdf.setFillColor(255, 255, 255);
  pdf.setDrawColor(218, 228, 240);
  pdf.roundedRect(leftX, cardY, cardWidth, execHeight, 3, 3, 'FD');
  pdf.roundedRect(rightX, cardY, cardWidth, execHeight, 3, 3, 'FD');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Top High-Priority Issues', leftX + 5, cardY + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Prioritised across all diagnostic tools.', leftX + 5, cardY + 14);

  let issueY = cardY + 24;
  if (topRows.length === 0) {
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(21, 128, 61);
    pdf.text('No high-priority issues detected.', leftX + 5, issueY);
  } else {
    topRows.forEach(({ issue, messageLines }, index) => {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8.5);
      pdf.setTextColor(scoreColor(issue.check.score ?? 0));
      pdf.text(`${index + 1}.`, leftX + 5, issueY);
      pdf.setTextColor(15, 54, 99);
      pdf.text(`${issue.scanLabel}: ${formatCheckName(issue.check.checkName)}`, leftX + 10, issueY);
      pdf.setTextColor(scoreColor(issue.check.score ?? 0));
      pdf.text(issue.check.severity, leftX + cardWidth - 5, issueY, { align: 'right' });
      issueY += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(82, 102, 132);
      pdf.text(messageLines, leftX + 10, issueY);
      issueY += messageLines.length * 4 + 2;
    });
  }

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Consolidated Score Progress', rightX + 5, cardY + 8);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(82, 102, 132);
  pdf.text('Estimated potential after priority fixes.', rightX + 5, cardY + 14);
  const barX = rightX + 5;
  const barY = cardY + 25;
  const barWidth = cardWidth - 10;
  pdf.setFillColor(230, 237, 245);
  pdf.roundedRect(barX, barY, barWidth, 3, 1.5, 1.5, 'F');
  pdf.setFillColor(22, 148, 71);
  pdf.roundedRect(barX, barY, Math.max(0, Math.min(100, summary.averageScore)) / 100 * barWidth, 3, 1.5, 1.5, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(21, 128, 61);
  pdf.text(`${summary.averageScore}`, barX, barY + 14);
  pdf.text(`${summary.potentialScore}`, barX + barWidth, barY + 14, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(82, 102, 132);
  pdf.text('/100 current consolidated score', barX + 8, barY + 14);
  pdf.text('/100 estimated potential', barX + barWidth - 8, barY + 14, { align: 'right' });
  pdf.setFillColor(240, 253, 244);
  pdf.roundedRect(barX, barY + 22, barWidth, 18, 2, 2, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7.5);
  pdf.setTextColor(22, 101, 52);
  pdf.text(pdf.splitTextToSize(`Opportunity: Resolve the ${Math.min(summary.topIssues.length, 5)} highest-priority cross-tool findings first, then rerun the full diagnostic report.`, barWidth - 6) as string[], barX + 3, barY + 28);
  y = cardY + execHeight + 12;

  y = drawConsolidatedActionPlan(pdf, summary.topIssues, margin, y, contentWidth);

  y = ensurePdfSpace(pdf, y, 52);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  pdf.text('Diagnostic Score Overview', margin, y);
  y += 8;
  const cardsPerRow = 2;
  const scoreCardGap = 6;
  const scoreCardWidth = (contentWidth - scoreCardGap) / cardsPerRow;
  const scoreCardHeight = 24;
  summary.entries.forEach(([scanType, scan], index) => {
    const column = index % cardsPerRow;
    const x = margin + column * (scoreCardWidth + scoreCardGap);
    if (column === 0) y = ensurePdfSpace(pdf, y, scoreCardHeight + 8);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(x, y, scoreCardWidth, scoreCardHeight, 3, 3, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(15, 54, 99);
    pdf.text(SCAN_LABELS[scanType] ?? scanType, x + 5, y + 8);
    pdf.setTextColor(scoreColor(scan.overallScore));
    pdf.text(`${scan.overallScore}/100`, x + scoreCardWidth - 5, y + 8, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(82, 102, 132);
    pdf.text(scoreLabel(scan.overallScore), x + 5, y + 16);
    if (column === cardsPerRow - 1 || index === summary.entries.length - 1) y += scoreCardHeight + 6;
  });

  y += 4;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(14);
  pdf.setTextColor(15, 54, 99);
  y = ensurePdfSpace(pdf, y, 16);
  pdf.text('Detailed Results', margin, y);
  y += 10;
  summary.entries.forEach(([scanType, scan], index) => {
    const scanSummary = buildScanSummary(scan);
    const scanTopRows = scanSummary.topIssues.slice(0, 3).map((check) => ({
      check,
      messageLines: (pdf.splitTextToSize(check.message ?? '', contentWidth - 18) as string[]).slice(0, 2),
    }));
    if (index > 0) y += 8;
    y = ensurePdfSpace(pdf, y, 36);
    pdf.setFillColor(248, 251, 254);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(margin, y, contentWidth, 14, 2, 2, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(15, 54, 99);
    pdf.text(SCAN_LABELS[scanType] ?? scanType, margin + 4, y + 7);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(82, 102, 132);
    pdf.text(`Score ${scan.overallScore}/100 | Grade ${scan.overallGrade}`, pageWidth - margin - 4, y + 8, { align: 'right' });
    y += 20;

    const scanExecHeight = 45 + (scanTopRows.length ? scanTopRows.reduce((sum, row) => sum + 6 + row.messageLines.length * 4, 0) : 8);
    y = ensurePdfSpace(pdf, y, scanExecHeight + 8);
    pdf.setFillColor(255, 255, 255);
    pdf.setDrawColor(218, 228, 240);
    pdf.roundedRect(margin, y, contentWidth, scanExecHeight, 3, 3, 'FD');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(15, 54, 99);
    pdf.text('Report Summary', margin + 5, y + 8);
    pdf.text('Top Issues', margin + 82, y + 8);
    pdf.text('Score Progress', margin + 142, y + 8);
    pdf.setFontSize(14);
    pdf.setTextColor(scoreColor(scan.overallScore));
    pdf.text(`${scan.overallScore}/100`, margin + 5, y + 19);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(82, 102, 132);
    pdf.text(`${scanSummary.passed} passed | ${scanSummary.warnings} warnings | ${scanSummary.failed} issues`, margin + 5, y + 27);
    pdf.text(`Potential score: ${scanSummary.potentialScore}/100`, margin + 5, y + 34);

    let scanIssueY = y + 18;
    if (!scanTopRows.length) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(21, 128, 61);
      pdf.text('No priority issues detected.', margin + 82, scanIssueY);
    } else {
      scanTopRows.forEach(({ check, messageLines }, index) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(scoreColor(check.score ?? 0));
        pdf.text(`${index + 1}. ${formatCheckName(check.checkName)}`, margin + 82, scanIssueY);
        pdf.setTextColor(82, 102, 132);
        pdf.setFont('helvetica', 'normal');
        pdf.text(messageLines, margin + 86, scanIssueY + 4);
        scanIssueY += 7 + messageLines.length * 4;
      });
    }

    const progressX = margin + 142;
    const progressY = y + 18;
    const progressWidth = contentWidth - 147;
    pdf.setFillColor(230, 237, 245);
    pdf.roundedRect(progressX, progressY, progressWidth, 3, 1.5, 1.5, 'F');
    pdf.setFillColor(22, 148, 71);
    pdf.roundedRect(progressX, progressY, Math.max(0, Math.min(100, scan.overallScore)) / 100 * progressWidth, 3, 1.5, 1.5, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(21, 128, 61);
    pdf.text(`${scan.overallScore}`, progressX, progressY + 12);
    pdf.text(`${scanSummary.potentialScore}`, progressX + progressWidth, progressY + 12, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(82, 102, 132);
    pdf.text('Current', progressX, progressY + 18);
    pdf.text('Potential', progressX + progressWidth, progressY + 18, { align: 'right' });
    y += scanExecHeight + 12;
    const checks = scan.checks ?? [];
    const actionableItems = checks.filter((check) => check.status === 'FAIL' || check.status === 'WARNING');
    const passedItems = checks.filter((check) => check.status === 'PASS');
    y = drawPdfPassedSummary(pdf, passedItems, margin, y, contentWidth);

    if (actionableItems.length) {
      y = ensurePdfSpace(pdf, y, 22);
      y = drawPdfTableHeader(pdf, margin, y, contentWidth);
    } else {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(21, 128, 61);
      pdf.text('No findings need attention in this report.', margin + 4, y + 2);
      y += 8;
    }

    actionableItems.forEach((check) => {
      const recommendation =
        (check.status === 'FAIL' || check.status === 'WARNING') && check.recommendation
          ? check.recommendation
          : '';
      const checkLines = (pdf.splitTextToSize(formatCheckName(check.checkName), 27) as string[]).slice(0, 3);
      const detailLines = pdf.splitTextToSize(check.message ?? '', 39) as string[];
      const recommendationLines = pdf.splitTextToSize(recommendation, contentWidth - 141) as string[];
      const rowHeight = Math.max(13, checkLines.length * 4 + 6, detailLines.length * 4 + 6, recommendationLines.length * 4 + 6);
      if (y + rowHeight > pdf.internal.pageSize.getHeight() - 26) {
        pdf.addPage();
        y = 32;
        y = drawPdfTableHeader(pdf, margin, y, contentWidth);
      }
      y = drawPdfDetailTableRow(pdf, check, margin, y, contentWidth);
    });
    y += 6;
  });

  const totalPages = pdf.getNumberOfPages();
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    pdf.setPage(pageNumber);
    drawConsolidatedPdfBranding(pdf, pageNumber, totalPages, title, logoDataUrl);
  }
  return pdf;
}

// ── sub-components ────────────────────────────────────────────────────────────

function ReportExecutiveSummary({ result }: { result: BrandingAnalyzeResponse }) {
  const summary = buildScanSummary(result);
  const topIssues = summary.topIssues.slice(0, 3);

  return (
    <div style={{ margin: '14px 0 18px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: '10px', marginBottom: '12px' }}>
        {[
          { label: 'Report Score', value: `${result.overallScore}/100`, color: scoreColor(result.overallScore) },
          { label: 'Potential Score', value: `${summary.potentialScore}/100`, color: '#15803d' },
          { label: 'Passed', value: summary.passed, color: '#15803d' },
          { label: 'Warnings', value: summary.warnings, color: '#d97706' },
          { label: 'Issues', value: summary.failed, color: '#dc2626' },
        ].map((item) => (
          <div key={item.label} style={{ border: '1px solid var(--asneris-border)', borderRadius: '10px', padding: '10px', background: '#fff' }}>
            <div style={{ fontSize: '11px', color: 'var(--asneris-muted)', fontWeight: 700 }}>{item.label}</div>
            <div style={{ marginTop: '5px', fontSize: '19px', fontWeight: 800, color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(220px, .9fr)', gap: '12px' }}>
        <section style={{ border: '1px solid var(--asneris-border)', borderRadius: '10px', padding: '14px', background: '#fff' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '15px', color: 'var(--asneris-navy)' }}>Top Issues</h3>
          {topIssues.length === 0 ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#166534', fontWeight: 700 }}>No priority issues detected.</p>
          ) : (
            topIssues.map((check, index) => (
              <div key={`${check.category}-${check.checkName}`} style={{ display: 'grid', gridTemplateColumns: '20px minmax(0, 1fr) auto', gap: '8px', padding: '8px 0', borderBottom: index === topIssues.length - 1 ? 'none' : '1px solid var(--asneris-border)' }}>
                <span style={{ color: scoreColor(check.score ?? 0), fontWeight: 800 }}>{index + 1}</span>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: 800, color: 'var(--asneris-navy)' }}>{formatCheckName(check.checkName)}</div>
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--asneris-muted)', lineHeight: 1.45 }}>{check.message}</p>
                </div>
                <span className={check.severity === 'HIGH' ? 'asneris-badge asneris-badge-error' : check.severity === 'MEDIUM' ? 'asneris-badge asneris-badge-warning' : 'asneris-badge asneris-badge-info'}>
                  {check.severity}
                </span>
              </div>
            ))
          )}
        </section>

        <section style={{ border: '1px solid var(--asneris-border)', borderRadius: '10px', padding: '14px', background: '#fff' }}>
          <h3 style={{ margin: '0 0 6px', fontSize: '15px', color: 'var(--asneris-navy)' }}>Score Progress</h3>
          <p style={{ margin: '0 0 14px', fontSize: '12px', color: 'var(--asneris-muted)' }}>Estimated improvement after priority fixes.</p>
          <div style={{ height: 9, borderRadius: 999, background: '#e6edf5', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, result.overallScore))}%`, background: '#169447' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px' }}>
            <div><strong style={{ color: '#15803d', fontSize: '16px' }}>{result.overallScore}</strong><span style={{ color: 'var(--asneris-muted)', fontSize: '11px' }}>/100</span><div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>Current</div></div>
            <div style={{ textAlign: 'right' }}><strong style={{ color: '#15803d', fontSize: '16px' }}>{summary.potentialScore}</strong><span style={{ color: 'var(--asneris-muted)', fontSize: '11px' }}>/100</span><div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>Potential</div></div>
          </div>
        </section>
      </div>
    </div>
  );
}

function ScanTypeResult({ scanType, result }: { scanType: string; result: BrandingAnalyzeResponse }) {
  const label = SCAN_LABELS[scanType] ?? scanType;
  const scoreBadge = result.overallScore != null
    ? `asneris-badge asneris-badge-${result.overallScore >= 70 ? 'success' : result.overallScore >= 40 ? 'warning' : 'error'}`
    : '';

  return (
    <details style={{ marginBottom: '8px', border: '1px solid var(--asneris-border)', borderRadius: '8px', overflow: 'hidden' }}>
      <summary style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', listStyle: 'none', userSelect: 'none' }}>
        <span style={{ fontWeight: 600, flex: 1 }}>{label}</span>
        {result.overallScore != null && (
          <span className={scoreBadge} style={{ fontSize: '12px' }}>Score {result.overallScore}</span>
        )}
        {result.overallGrade && (
          <span className="asneris-badge" style={{ fontSize: '12px' }}>Grade {result.overallGrade}</span>
        )}
      </summary>
      <div style={{ padding: '0 16px 16px' }}>
        <ScanResultSummary result={result} />
        <ReportExecutiveSummary result={result} />
        <hr className="asneris-divider" />
        <CheckCategoryAccordion checks={result.checks} />
      </div>
    </details>
  );
}

function ConsolidatedExecutiveSummary({ scanResults }: { scanResults?: Record<string, BrandingAnalyzeResponse> }) {
  const summary = buildConsolidatedSummary(scanResults);
  const topIssues = summary.topIssues.slice(0, 5);

  if (!summary.entries.length) return null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <section className="asneris-report-card" style={{ padding: '18px', marginBottom: '16px' }}>
        <h2 style={{ margin: '0 0 14px', fontSize: '18px', color: 'var(--asneris-navy)' }}>
          Consolidated Summary
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))', gap: '12px' }}>
          {[
            { label: 'Overall Score', value: `${summary.averageScore}/100`, color: scoreColor(summary.averageScore) },
            { label: 'Potential Score', value: `${summary.potentialScore}/100`, color: '#15803d' },
            { label: 'Reports Included', value: summary.entries.length, color: 'var(--asneris-navy)' },
            { label: 'Warnings', value: summary.warnings, color: '#d97706' },
            { label: 'Issues', value: summary.failed, color: '#dc2626' },
          ].map((item) => (
            <div key={item.label} style={{ border: '1px solid var(--asneris-border)', borderRadius: '10px', padding: '12px', background: '#fff' }}>
              <div style={{ fontSize: '11px', color: 'var(--asneris-muted)', fontWeight: 700 }}>{item.label}</div>
              <div style={{ marginTop: '6px', fontSize: '22px', fontWeight: 800, color: item.color }}>{item.value}</div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(260px, .9fr)', gap: '16px' }}>
        <section className="asneris-report-card" style={{ padding: '18px' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: '17px', color: 'var(--asneris-navy)' }}>
            Top High-Priority Issues
          </h2>
          <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--asneris-muted)' }}>
            Prioritised across all diagnostic tools by severity and score.
          </p>
          {topIssues.length === 0 ? (
            <div style={{ padding: '14px', borderRadius: '10px', background: '#ecfdf5', color: '#166534', fontWeight: 700 }}>
              No high-priority issues detected.
            </div>
          ) : (
            topIssues.map(({ scanLabel, check }, index) => (
              <div key={`${scanLabel}-${check.category}-${check.checkName}`} style={{ display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr) auto', gap: '10px', padding: '10px 0', borderBottom: index === topIssues.length - 1 ? 'none' : '1px solid var(--asneris-border)' }}>
                <span style={{ color: scoreColor(check.score ?? 0), fontWeight: 800 }}>{index + 1}</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--asneris-navy)' }}>{scanLabel}: {formatCheckName(check.checkName)}</div>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--asneris-muted)', lineHeight: 1.5 }}>{check.message}</p>
                </div>
                <span className={check.severity === 'HIGH' ? 'asneris-badge asneris-badge-error' : check.severity === 'MEDIUM' ? 'asneris-badge asneris-badge-warning' : 'asneris-badge asneris-badge-info'}>
                  {check.severity}
                </span>
              </div>
            ))
          )}
        </section>

        <section className="asneris-report-card" style={{ padding: '18px' }}>
          <h2 style={{ margin: '0 0 6px', fontSize: '17px', color: 'var(--asneris-navy)' }}>
            Consolidated Score Progress
          </h2>
          <p style={{ margin: '0 0 18px', fontSize: '12px', color: 'var(--asneris-muted)' }}>
            Estimated potential after resolving priority cross-tool findings.
          </p>
          <div style={{ height: 10, borderRadius: 999, background: '#e6edf5', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, summary.averageScore))}%`, background: '#169447' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px' }}>
            <div><strong style={{ color: '#15803d', fontSize: '18px' }}>{summary.averageScore}</strong><span style={{ color: 'var(--asneris-muted)', fontSize: '12px' }}>/100</span><div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>Current consolidated score</div></div>
            <div style={{ textAlign: 'right' }}><strong style={{ color: '#15803d', fontSize: '18px' }}>{summary.potentialScore}</strong><span style={{ color: 'var(--asneris-muted)', fontSize: '12px' }}>/100</span><div style={{ fontSize: '11px', color: 'var(--asneris-muted)' }}>Estimated potential</div></div>
          </div>
          <div style={{ marginTop: '18px', padding: '14px', borderRadius: '10px', background: '#f0fdf4', color: '#166534', fontSize: '12px', fontWeight: 700, lineHeight: 1.45 }}>
            Resolve the highest-priority cross-tool findings first, then rerun the full diagnostic report.
          </div>
        </section>
      </div>
    </div>
  );
}

// ── Option 1: new request form ────────────────────────────────────────────────

function NewRequestPanel({ onGoToStep3 }: { onGoToStep3: (refNum: string, email: string) => void }) {
  const [step, setStep]       = useState<1 | 2>(1);
  const [name, setName]       = useState('');
  const [businessName, setBusinessName] = useState('');
  const [url, setUrl]         = useState('');
  const [email, setEmail]     = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState<ConsolidatedReportCreateResponse | null>(null);
  const [copied, setCopied]   = useState(false);

  const canContinueStep1 = name.trim().length > 0 && businessName.trim().length > 0 && isValidEmail(email);
  const canSubmit = isValidUrl(url) && canContinueStep1 && !loading;

  async function handleSubmit() {
    setLoading(true);
    setError('');
    try {
      const data = await requestConsolidatedReport(
        name.trim(),
        url.trim(),
        email.trim(),
        businessName.trim(),
      );
      setResult(data);
    } catch (err) {
      setError(extractErrorMessages(err));
    } finally {
      setLoading(false);
    }
  }

  function handleCopy() {
    if (!result) return;
    navigator.clipboard.writeText(result.referenceNumber);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (result) {
    return (
      <div>
        <div className="asneris-alert asneris-alert--success" style={{ marginBottom: '24px' }}>
          <p className="asneris-alert-title">Request registered!</p>
          <p className="asneris-alert-body">{result.message}</p>
        </div>

        <div style={{ padding: '24px', border: '1px solid var(--asneris-border)', borderRadius: '10px', textAlign: 'center' }}>
          <p style={{ margin: '0 0 8px', fontSize: '12px', color: 'var(--asneris-muted)' }}>Your reference number</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <span style={{ fontSize: '28px', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--asneris-blue)' }}>
              {result.referenceNumber}
            </span>
            <button
              type="button"
              title={copied ? 'Copied!' : 'Copy reference number'}
              onClick={handleCopy}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--asneris-muted)' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <rect x="9" y="9" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
          </div>
          {result.estimatedReadyAt && (
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--asneris-muted)' }}>
              Estimated ready by <strong>{formatDateTime(result.estimatedReadyAt)}</strong>
            </p>
          )}
          <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--asneris-muted)' }}>
            Save this reference number — you'll need it to retrieve your report.
            We'll also email you a copy when it's ready.
          </p>
        </div>

        <button
          type="button"
          className="asneris-btn asneris-btn-primary asneris-btn-sm"
          style={{ marginTop: '16px', marginRight: '8px' }}
          onClick={() => onGoToStep3(result.referenceNumber, email.trim())}
        >
          Proceed to Step 3: View Generated Report
        </button>

        <button
          type="button"
          className="asneris-btn asneris-btn-secondary asneris-btn-sm"
          style={{ marginTop: '16px' }}
          onClick={() => {
            setResult(null);
            setStep(1);
            setName('');
            setBusinessName('');
            setUrl('');
            setEmail('');
          }}
        >
          Submit another request
        </button>

        <PublicCtaBox learnMoreHref={CONSOLIDATED_LEARN_MORE_HREF} />
      </div>
    );
  }

  return (
    <div>
      <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--asneris-muted)' }}>
        Follow the 3-step flow: add your details, add your website URL, then use your reference
        number to retrieve the generated report.
      </p>

      <div style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--asneris-muted)', fontWeight: 600 }}>
        Step {step} of 3
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {step === 1 && (
          <>
            <div className="asneris-field">
              <label className="asneris-field-label" htmlFor="new-req-name">Step 1: Your Name</label>
              <input
                id="new-req-name"
                className={`asneris-input${name.length > 0 && name.trim().length === 0 ? ' asneris-input--error' : ''}`}
                type="text"
                placeholder="Your full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="asneris-field">
              <label className="asneris-field-label" htmlFor="new-req-email">Email address</label>
              <input
                id="new-req-email"
                className={`asneris-input${email.length > 0 && !isValidEmail(email) ? ' asneris-input--error' : ''}`}
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              {email.length > 0 && !isValidEmail(email) && (
                <span className="asneris-field-helper asneris-field-helper--error">Must be a valid email address</span>
              )}
            </div>
            <div className="asneris-field">
              <label className="asneris-field-label" htmlFor="new-req-business">Business Name</label>
              <input
                id="new-req-business"
                className={`asneris-input${businessName.length > 0 && businessName.trim().length === 0 ? ' asneris-input--error' : ''}`}
                type="text"
                placeholder="Your business name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                disabled={loading}
              />
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="asneris-field">
              <label className="asneris-field-label" htmlFor="new-req-url">Step 2: Website URL</label>
              <input
                id="new-req-url"
                className={`asneris-input${url.length > 0 && !isValidUrl(url) ? ' asneris-input--error' : ''}`}
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={loading}
              />
              {url.length > 0 && !isValidUrl(url) && (
                <span className="asneris-field-helper asneris-field-helper--error">Must be a valid http:// or https:// URL</span>
              )}
            </div>

            <p style={{ margin: 0, fontSize: '12px', color: 'var(--asneris-muted)' }}>
              Max allowed tries: <strong>{MAX_ALLOWED_TRIES}</strong>
            </p>
          </>
        )}

        {error && (
          <div className="asneris-alert asneris-alert--error">
            <p className="asneris-alert-body">{error}</p>
          </div>
        )}

        {step === 1 ? (
          <button
            type="button"
            className="asneris-btn asneris-btn-primary"
            disabled={!canContinueStep1 || loading}
            onClick={() => { setError(''); setStep(2); }}
            style={{ alignSelf: 'flex-start' }}
          >
            Continue to Step 2
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="asneris-btn asneris-btn-secondary"
              disabled={loading}
              onClick={() => { setError(''); setStep(1); }}
            >
              Back to Step 1
            </button>
            <button
              type="button"
              className="asneris-btn asneris-btn-primary"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              {loading
                ? <><span className="asneris-spinner asneris-spinner--sm" style={{ marginRight: '8px' }} />Registering…</>
                : '📋 Request Consolidated Report'
              }
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Option 2: status check form ───────────────────────────────────────────────

function CheckStatusPanel({ initialRefNum, initialEmail }: { initialRefNum?: string; initialEmail?: string }) {
  const [refNum, setRefNum]   = useState(initialRefNum ?? '');
  const [email, setEmail]     = useState(initialEmail ?? '');
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [error, setError]     = useState('');
  const [result, setResult]   = useState<ConsolidatedReportStatusResponse | null>(null);

  useEffect(() => {
    if (initialRefNum) setRefNum(initialRefNum);
  }, [initialRefNum]);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  const canSubmit = refNum.trim().length > 0 && isValidEmail(email) && !loading;

  async function handleCheck() {
    setLoading(true);
    setError('');
    try {
      const data = await checkConsolidatedReportStatus(refNum.trim().toUpperCase(), email.trim());
      setResult(data);
    } catch (err: unknown) {
      const msg = extractErrorMessages(err);
      if (
        typeof err === 'object' &&
        err !== null &&
        'response' in err &&
        (err as { response?: { status?: number } }).response?.status === 404
      ) {
        setError('No report found for that reference number and email. Please check and try again.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDownloadPdf() {
    if (!result || result.reportState !== 'READY' || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const logoDataUrl = await loadImageDataUrl(ASNERIS_LOGO_PATH);
      const pdf = generateConsolidatedPdf(result, email.trim(), logoDataUrl);
      pdf.save(`${result.referenceNumber.toLowerCase()}-full-seo-diagnostic-report.pdf`);
    } catch (err) {
      console.error('Consolidated PDF download failed:', err);
      window.alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  }

  function renderStatus() {
    if (!result) return null;

    switch (result.reportState) {
      case 'PROCESSING_WAIT':
        return (
          <div className="asneris-alert asneris-alert--info">
            <p className="asneris-alert-title">⏳ Report in progress</p>
            <p className="asneris-alert-body">
              {result.message}
              {result.estimatedReadyAt && (
                <><br /><small>Estimated ready by {formatDateTime(result.estimatedReadyAt)}</small></>
              )}
            </p>
          </div>
        );

      case 'PROCESSING_DELAYED':
        return (
          <div className="asneris-alert asneris-alert--warning">
            <p className="asneris-alert-title">⚠ Taking a little longer</p>
            <p className="asneris-alert-body">{result.message}</p>
          </div>
        );

      case 'FAILED':
        return (
          <div className="asneris-alert asneris-alert--error">
            <p className="asneris-alert-title">Processing error</p>
            <p className="asneris-alert-body">{result.message}</p>
          </div>
        );

      case 'READY': {
        const scans = result.scanResults ?? {};
        return (
          <div>
            <div className="asneris-alert asneris-alert--success" style={{ marginBottom: '24px' }}>
              <p className="asneris-alert-title">
                {result.emailSent ? '✓ Report ready — also sent to your email!' : '✓ Report ready!'}
              </p>
              <p className="asneris-alert-body">
                {result.message}
                {result.completedAt && (
                  <><br /><small>Completed {formatDateTime(result.completedAt)}</small></>
                )}
              </p>
            </div>

            <p style={{ margin: '0 0 12px', fontWeight: 600, fontSize: '16px', color: 'var(--asneris-navy)' }}>
              Consolidated Diagnostic Results
            </p>

            <ConsolidatedExecutiveSummary scanResults={scans} />

            {SCAN_ORDER.filter((t) => scans[t]).map((scanType) => (
              <ScanTypeResult key={scanType} scanType={scanType} result={scans[scanType]} />
            ))}

            <PublicCtaBox
              onDownload={handleDownloadPdf}
              downloadLabel={downloadingPdf ? 'Preparing PDF...' : 'Download PDF'}
              downloadDisabled={downloadingPdf}
              learnMoreHref={CONSOLIDATED_LEARN_MORE_HREF}
            />
          </div>
        );
      }

      default:
        return null;
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '16px', fontSize: '13px', color: 'var(--asneris-muted)', fontWeight: 600 }}>
        Step 3 of 3
      </div>

      <p style={{ margin: '0 0 24px', fontSize: '14px', color: 'var(--asneris-muted)' }}>
        Enter the reference number you received when you submitted your request, along with the
        same email address.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div className="asneris-field">
          <label className="asneris-field-label" htmlFor="status-ref-num">Reference number</label>
          <input
            id="status-ref-num"
            className="asneris-input"
            type="text"
            placeholder="RPT-K7MN4X2P"
            value={refNum}
            onChange={(e) => setRefNum(e.target.value.toUpperCase())}
            style={{ letterSpacing: '0.1em', fontWeight: 600 }}
            disabled={loading}
          />
        </div>
        <div className="asneris-field">
          <label className="asneris-field-label" htmlFor="status-email">Email address</label>
          <input
            id="status-email"
            className={`asneris-input${email.length > 0 && !isValidEmail(email) ? ' asneris-input--error' : ''}`}
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />
          {email.length > 0 && !isValidEmail(email) && (
            <span className="asneris-field-helper asneris-field-helper--error">Must be a valid email address</span>
          )}
        </div>

        {error && (
          <div className="asneris-alert asneris-alert--error">
            <p className="asneris-alert-body">{error}</p>
          </div>
        )}

        <button
          type="button"
          className="asneris-btn asneris-btn-primary"
          disabled={!canSubmit}
          onClick={handleCheck}
          style={{ alignSelf: 'flex-start' }}
        >
          {loading
            ? <><span className="asneris-spinner asneris-spinner--sm" style={{ marginRight: '8px' }} />Checking…</>
            : '🔍 Check Report Status'
          }
        </button>
      </div>

      {result && <div style={{ marginTop: '24px' }}>{renderStatus()}</div>}
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────────

export default function ConsolidatedReportWidget() {
  const [activeTab, setActiveTab] = useState(0);
  const [step3Reference, setStep3Reference] = useState('');
  const [step3Email, setStep3Email] = useState('');

  function handleGoToStep3(refNum: string, email: string) {
    setStep3Reference(refNum);
    setStep3Email(email);
    setActiveTab(1);
  }

  return (
    <div>
      <div className="asneris-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          className={`asneris-tab${activeTab === 0 ? ' is-active' : ''}`}
          aria-selected={activeTab === 0}
          onClick={() => setActiveTab(0)}
        >
          📋 New Report
        </button>
        <button
          type="button"
          role="tab"
          className={`asneris-tab${activeTab === 1 ? ' is-active' : ''}`}
          aria-selected={activeTab === 1}
          onClick={() => setActiveTab(1)}
        >
          🔍 Check Existing Report
        </button>
      </div>

      <div role="tabpanel" hidden={activeTab !== 0}>
        <NewRequestPanel onGoToStep3={handleGoToStep3} />
      </div>
      <div role="tabpanel" hidden={activeTab !== 1}>
        <CheckStatusPanel initialRefNum={step3Reference} initialEmail={step3Email} />
      </div>
    </div>
  );
}
